import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK = "Our assistant is offline for a moment. Email hello@vynlworld.com and a human will help.";

const genId = () => {
  try { return crypto.randomUUID(); } catch (e) { return "s-" + Math.random().toString(36).slice(2) + Date.now(); }
};

export const ChatWidget = ({ onApply }) => {
  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(false);
  const [sessionId] = useState(genId);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hey — I'm the VYNLWORLD assistant. Ask me anything about how it works, or how to become a founding artist." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const openPanel = useCallback(() => {
    setOpen(true);
    if (!opened) {
      setOpened(true);
      axios.post(`${API}/chat/open`).catch(() => {});
    }
  }, [opened]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/chat`, { session_id: sessionId, message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply || FALLBACK }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", content: FALLBACK }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <motion.button
        data-testid="chat-fab"
        onClick={() => (open ? setOpen(false) : openPanel())}
        aria-label={open ? "Close assistant" : "Open assistant"}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        whileHover={{ y: -2 }}
        className="fixed bottom-5 right-5 z-[60] flex items-center justify-center h-12 w-12 rounded-full shadow-lg"
        style={{ backgroundColor: "#C724B1", color: "#F4EEF7", boxShadow: "0 10px 30px rgba(199,36,177,0.35)" }}
      >
        {open ? <X size={20} /> : <MessageCircle size={20} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed z-[60] flex flex-col bottom-20 right-4 left-4 sm:left-auto sm:right-5 sm:w-[380px] max-h-[72vh] h-[520px]"
            style={{ backgroundColor: "#0E0A16", border: "1px solid #2A2233" }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: "1px solid #2A2233" }}>
              <div>
                <div className="font-display text-sm uppercase tracking-[0.2em]" style={{ color: "#F4EEF7" }}>VYNLWORLD Assistant</div>
                <div className="text-[11px]" style={{ color: "#9B8FAC" }}>Ask about how it works</div>
              </div>
              <button data-testid="chat-close" onClick={() => setOpen(false)} aria-label="Close" style={{ color: "#9B8FAC" }} className="hover:text-[#F4EEF7] transition-colors">
                <X size={18} />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className="max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed"
                    style={
                      m.role === "user"
                        ? { backgroundColor: "#C724B1", color: "#F4EEF7" }
                        : { backgroundColor: "#1A1424", color: "#F4EEF7", border: "1px solid #2A2233" }
                    }
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {busy && (
                <div className="flex justify-start">
                  <div className="px-3.5 py-2.5 text-sm" style={{ backgroundColor: "#1A1424", color: "#9B8FAC", border: "1px solid #2A2233" }}>…</div>
                </div>
              )}
            </div>

            {/* Steer-to-form shortcut */}
            <button
              data-testid="chat-apply-link"
              onClick={() => { setOpen(false); onApply?.(); }}
              className="flex items-center justify-center gap-2 px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] transition-colors"
              style={{ color: "#F0C420", borderTop: "1px solid #2A2233" }}
            >
              Become a founding artist <ArrowRight size={13} />
            </button>

            {/* Input */}
            <form onSubmit={send} className="flex items-center gap-2 p-3" style={{ borderTop: "1px solid #2A2233" }}>
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type a message…"
                className="flex-1 px-3 py-2.5 text-sm outline-none focus:border-[#C724B1] transition-colors"
                style={{ backgroundColor: "#1A1424", border: "1px solid #2A2233", color: "#F4EEF7" }}
              />
              <button data-testid="chat-send" type="submit" disabled={busy} aria-label="Send" className="btn-sharp flex items-center justify-center h-10 w-10 disabled:opacity-50" style={{ backgroundColor: "#C724B1", color: "#F4EEF7" }}>
                <Send size={16} />
              </button>
            </form>

            {/* AI + privacy notice */}
            <div className="px-4 pb-3 text-xs leading-snug" style={{ color: "#9B8FAC" }}>
              Messages are processed by an AI and stored anonymously.{" "}
              <a href="/privacy" data-testid="chat-privacy-link" className="underline hover:text-[#F4EEF7]" style={{ color: "#F4EEF7" }}>Privacy</a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
